import { useId, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { StatusBadge } from '@pazportal/ui';
import { useActiveTrip, type ActiveTrip } from '../hooks/useActiveTrip';
import { useDailyReports, dailyReportsQueryKey, reportStatus, type DailyReport } from '../hooks/useDailyReports';
import { siteIntelQueryKey } from '../hooks/useSiteIntel';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toaster';
import { TripPicker } from '../components/TripPicker';
import { ErrorBlock, LoadingBlock, NoActiveTrip } from '../components/PageState';
import { supabase } from '../lib/supabase';
import { laborHoursError, parseLaborHours } from '../lib/laborHours';
import { formatDate, formatDateTime, formatSolutionType, pluralize, timeAgo } from '../lib/formatters';
import { localDateString, reportDayNumber } from '../lib/tripDates';
import { persistOrQueue, queuedMessage } from '../lib/mutations';
import { replayOutbox } from '../lib/offlineOutbox';
import { isOnline, friendlyDbError } from '../lib/errors';
import { INTEL_MIN_CHARS, INTEL_PRIORITIES, SOLUTION_TYPES, type IntelPriority, type SolutionType } from '../lib/solutions';

export function DailyReportPage() {
  const { data: trip, trips, isLoading, isError, error, refetch, selectTrip } = useActiveTrip();
  const reportsQuery = useDailyReports(trip?.id);

  if (isLoading) return <LoadingBlock label="Loading your trips…" />;
  if (!trip) {
    return <NoActiveTrip trips={trips} onSelect={selectTrip} error={isError ? error : undefined} onRetry={() => void refetch()} />;
  }
  const reports = reportsQuery.data ?? [];
  const today = localDateString();
  const existing = reports.find((r) => r.report_date === today) ?? null;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      {trips.length > 1 && <TripPicker trips={trips} selectedId={trip.id} onSelect={selectTrip} />}
      {reportsQuery.isLoading ? (
        <LoadingBlock label="Loading today's report…" />
      ) : reportsQuery.isError ? (
        <ErrorBlock title="Could not load this trip's reports" error={reportsQuery.error} onRetry={() => void reportsQuery.refetch()} />
      ) : (
        /* Keyed so the form re-initialises when the trip, the day, or the stored row changes. */
        <DailyReportForm key={`${trip.id}:${today}:${existing?.id ?? 'new'}`} trip={trip} reports={reports} existing={existing} today={today} />
      )}
    </div>
  );
}

function DailyReportForm({
  trip,
  reports,
  existing,
  today,
}: {
  trip: ActiveTrip;
  reports: DailyReport[];
  existing: DailyReport | null;
  today: string;
}) {
  const { profileId } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();

  const dayNumber = reportDayNumber(
    existing?.day_number,
    trip.start_date,
    reports.map((r) => r.day_number),
    today,
  );

  const [hours, setHours] = useState(existing?.hours_on_site ?? '');
  const [laborHours, setLaborHours] = useState(existing?.labor_hours != null ? String(existing.labor_hours) : '');
  const [laborTouched, setLaborTouched] = useState(false);
  const [crew, setCrew] = useState(existing?.crew_on_site ?? '');
  const [workCompleted, setWorkCompleted] = useState(existing?.work_completed ?? '');
  const [tomorrowPlan, setTomorrowPlan] = useState(existing?.tomorrow_plan ?? '');
  const [internalNotes, setInternalNotes] = useState(existing?.internal_notes ?? '');
  const [punchList, setPunchList] = useState(existing?.punch_list ?? '');
  const [leadCaptured, setLeadCaptured] = useState(existing?.lead_captured ?? false);
  const [leadSolution, setLeadSolution] = useState<SolutionType>(SOLUTION_TYPES[0]);
  const [leadPriority, setLeadPriority] = useState<IntelPriority>('medium');
  const [leadObservation, setLeadObservation] = useState('');
  const [share, setShare] = useState(Boolean(existing?.delivered_to_customer_at));
  const [busy, setBusy] = useState<'draft' | 'submit' | null>(null);
  const [validation, setValidation] = useState<string | null>(null);

  const alreadyCaptured = Boolean(existing?.lead_captured);
  const observationLength = leadObservation.trim().length;
  const wantsIntelInsert = leadCaptured && observationLength > 0;
  const leadNeedsObservation = leadCaptured && !alreadyCaptured && observationLength < INTEL_MIN_CHARS;
  const status = existing ? reportStatus(existing) : null;

  const save = async (mode: 'draft' | 'submit') => {
    setValidation(null);
    const hoursProblem = laborHoursError(laborHours, mode);
    if (hoursProblem) {
      setLaborTouched(true);
      setValidation(hoursProblem);
      return;
    }
    if (mode === 'submit' && leadNeedsObservation) {
      setValidation(`Describe the lead in at least ${INTEL_MIN_CHARS} characters, or untick "Lead captured".`);
      return;
    }
    if (wantsIntelInsert && observationLength < INTEL_MIN_CHARS) {
      setValidation(`Lead observations need at least ${INTEL_MIN_CHARS} characters.`);
      return;
    }
    if (wantsIntelInsert && !profileId) {
      setValidation('Your staff profile could not be resolved, so the lead cannot be attributed. Save the report without the lead and add it from Site intel later.');
      return;
    }

    setBusy(mode);
    const now = new Date().toISOString();
    const submittedAt = mode === 'submit' ? (existing?.submitted_at ?? now) : (existing?.submitted_at ?? null);
    const deliveredAt =
      mode === 'submit'
        ? share
          ? (existing?.delivered_to_customer_at ?? now)
          : null
        : (existing?.delivered_to_customer_at ?? null);

    const payload = {
      trip_id: trip.id,
      report_date: today,
      day_number: dayNumber,
      hours_on_site: hours.trim() || null,
      labor_hours: parseLaborHours(laborHours),
      crew_on_site: crew.trim() || null,
      work_completed: workCompleted.trim() || null,
      tomorrow_plan: tomorrowPlan.trim() || null,
      internal_notes: internalNotes.trim() || null,
      punch_list: punchList.trim() || null,
      lead_captured: leadCaptured,
      submitted_at: submittedAt,
      delivered_to_customer_at: deliveredAt,
    };

    try {
      const outcome = await persistOrQueue('daily_report_upsert', payload, () =>
        supabase.from('daily_reports').upsert(payload, { onConflict: 'trip_id,report_date' }).select('id').single(),
      );

      if (outcome.status === 'failed') {
        notify(`Could not save the report: ${outcome.message}`, 'error');
        return;
      }

      const what = mode === 'submit' ? 'The report' : 'The draft';
      if (outcome.status === 'queued') {
        notify(queuedMessage(what, outcome), 'warning');
      } else if (mode === 'draft') {
        notify('Draft saved.', 'success');
      } else if (share) {
        notify('Report submitted and shared with the customer portal.', 'success');
      } else {
        notify('Report submitted. It is not shared with the customer.', 'success');
      }

      if (wantsIntelInsert && trip.location?.id && profileId) {
        const intelPayload = {
          location_id: trip.location.id,
          trip_id: trip.id,
          observation: leadObservation.trim(),
          solution_type: leadSolution,
          priority: leadPriority,
          tags: [] as string[],
          is_confidential: true,
          captured_by_user_id: profileId,
        };
        const intel = await persistOrQueue('site_intel_insert', intelPayload, () =>
          supabase.from('site_intel').insert(intelPayload).select('id').single(),
        );
        if (intel.status === 'failed') notify(`Report saved, but the lead was not recorded: ${intel.message}`, 'error');
        else if (intel.status === 'queued') notify(queuedMessage('The lead', intel), 'warning');
        else {
          notify('Lead recorded in Site intel.', 'success');
          setLeadObservation('');
          void qc.invalidateQueries({ queryKey: siteIntelQueryKey(trip.location.id) });
        }
      }

      if (outcome.status === 'saved' && mode === 'submit' && trip.status === 'scheduled' && isOnline()) {
        const { error: tripError } = await supabase.from('field_trips').update({ status: 'in_progress' }).eq('id', trip.id);
        if (tripError) notify(`Report saved, but the trip could not be marked in progress: ${friendlyDbError(tripError)}`, 'warning');
        else void qc.invalidateQueries({ queryKey: ['active-trips'] });
      }

      if (outcome.status === 'saved') {
        void qc.invalidateQueries({ queryKey: dailyReportsQueryKey(trip.id) });
      } else if (isOnline()) {
        void replayOutbox();
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        void save('submit');
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-text">Daily report — Day {dayNumber}</h1>
        <p className="mt-0.5 text-sm text-text-muted">
          <span className="font-mono">{trip.project_number}</span> · {formatDate(today)}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-text-muted">
          {existing && status ? (
            <>
              <StatusBadge status={status} />
              <span>Today&apos;s report was last updated {timeAgo(existing.updated_at)}.</span>
            </>
          ) : (
            <span>No report filed for today yet.</span>
          )}
          {reports.length > 0 && <span>· {pluralize(reports.length, 'report')} filed on this trip</span>}
        </div>
      </div>

      <section className="card space-y-4 p-4" aria-labelledby="customer-section">
        <div>
          <h2 id="customer-section" className="card-title">
            Customer-facing report
          </h2>
          <p className="mt-1 text-xs text-text-muted">These fields appear in the customer portal when the report is shared.</p>
        </div>
        <Field label="Hours on site" hint="e.g. 7:30 AM – 5:30 PM">
          {(id) => <input id={id} value={hours} onChange={(e) => setHours(e.target.value)} className="input-field" autoComplete="off" />}
        </Field>
        <Field label="Crew on site">
          {(id) => <input id={id} value={crew} onChange={(e) => setCrew(e.target.value)} className="input-field" autoComplete="off" />}
        </Field>
        <Field label="Work completed today">
          {(id) => <textarea id={id} value={workCompleted} onChange={(e) => setWorkCompleted(e.target.value)} rows={4} className="input-field" />}
        </Field>
        <Field label="Tomorrow's plan" hint="Shown as next planned work on the overview">
          {(id) => <textarea id={id} value={tomorrowPlan} onChange={(e) => setTomorrowPlan(e.target.value)} rows={3} className="input-field" />}
        </Field>
      </section>

      <section className="space-y-4 rounded-lg border-l-4 border-accent-purple bg-accent-purple-soft p-4" aria-labelledby="internal-section">
        <div>
          <h2 id="internal-section" className="text-xs font-semibold uppercase tracking-wider text-accent-purple">
            Internal notes — PazNetworks only
          </h2>
          <p className="mt-1 text-xs text-accent-purple/80">Never shared with the customer.</p>
        </div>
        <Field
          label="Labor hours"
          hint="total crew-hours today, e.g. 2 techs × 8 h = 16 · used for billing, not shown in the customer portal"
          error={laborTouched ? (laborHoursError(laborHours, 'draft') ?? undefined) : undefined}
        >
          {(id, invalid) => (
            <input
              id={id}
              value={laborHours}
              onChange={(e) => setLaborHours(e.target.value)}
              onBlur={() => setLaborTouched(true)}
              inputMode="decimal"
              aria-invalid={invalid || undefined}
              aria-required="true"
              className="input-field"
              autoComplete="off"
            />
          )}
        </Field>
        <Field label="Technical anomalies / internal observations">
          {(id) => <textarea id={id} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} rows={3} className="input-field" />}
        </Field>
        <Field label="Punch list">
          {(id) => <textarea id={id} value={punchList} onChange={(e) => setPunchList(e.target.value)} rows={3} className="input-field" />}
        </Field>

        <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm text-text">
          <input type="checkbox" checked={leadCaptured} onChange={(e) => setLeadCaptured(e.target.checked)} className="checkbox-field" />
          <span>
            Lead captured
            {alreadyCaptured && <span className="ml-1 text-xs text-text-muted">(already recorded for today)</span>}
          </span>
        </label>

        {leadCaptured && (
          <div className="space-y-3 border-l-2 border-accent-purple/30 pl-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Solution type">
                {(id) => (
                  <select id={id} value={leadSolution} onChange={(e) => setLeadSolution(e.target.value as SolutionType)} className="input-field">
                    {SOLUTION_TYPES.map((s) => (
                      <option key={s} value={s}>
                        {formatSolutionType(s)}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="Priority">
                {(id) => (
                  <select id={id} value={leadPriority} onChange={(e) => setLeadPriority(e.target.value as IntelPriority)} className="input-field">
                    {INTEL_PRIORITIES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </div>
            <Field
              label={alreadyCaptured ? 'Additional observation (optional)' : 'Observation'}
              hint={`at least ${INTEL_MIN_CHARS} characters`}
              error={observationLength > 0 && observationLength < INTEL_MIN_CHARS ? `${INTEL_MIN_CHARS - observationLength} more characters needed` : undefined}
            >
              {(id, invalid) => (
                <textarea id={id} value={leadObservation} onChange={(e) => setLeadObservation(e.target.value)} rows={3} className="input-field" aria-invalid={invalid} />
              )}
            </Field>
          </div>
        )}
      </section>

      <section className="card space-y-3 p-4" aria-labelledby="share-section">
        <h2 id="share-section" className="card-title">
          Sharing
        </h2>
        <label className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm text-text">
          <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} className="checkbox-field mt-0.5" />
          <span>
            <span className="font-medium">Share this report with the customer</span>
            <span className="mt-0.5 block text-xs text-text-muted">
              When submitted, the customer-facing section becomes visible in the customer portal. Internal notes and the punch list are never shared.
              {existing?.delivered_to_customer_at && !share ? ' Unticking removes it from the portal on submit.' : ''}
            </span>
          </span>
        </label>
      </section>

      {validation && (
        <p role="alert" className="input-error text-sm">
          {validation}
        </p>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <button type="button" onClick={() => void save('draft')} disabled={busy !== null} className="btn-secondary flex-1">
          {busy === 'draft' ? 'Saving…' : 'Save draft'}
        </button>
        <button type="submit" disabled={busy !== null} className="btn-primary flex-1">
          {busy === 'submit' ? 'Submitting…' : share ? 'Submit and share' : 'Submit report'}
        </button>
      </div>
      {existing?.submitted_at && (
        <p className="text-xs text-text-muted">
          First submitted {formatDateTime(existing.submitted_at)}
          {existing.delivered_to_customer_at ? ` · shared with the customer ${timeAgo(existing.delivered_to_customer_at)}` : ' · not shared with the customer'}. Submitting again updates the same report.
        </p>
      )}
    </form>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: (id: string, invalid: boolean) => ReactNode;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="input-label">
        {label}
        {hint && <span className="font-normal text-text-faint"> ({hint})</span>}
      </label>
      {children(id, Boolean(error))}
      {error && (
        <span className="input-error" role="status">
          {error}
        </span>
      )}
    </div>
  );
}
